/**
 * BypassModeBanner — admin-only warning that LESSON_REVIEW_BYPASS_PAYMENT
 * is active in production.
 *
 * Why: bypass mode is an operational unblock. Without a visible reminder
 * it can be left on indefinitely. This banner is the safety net.
 *
 * Visibility rules (all must be true):
 *   • The current user has role === "admin"
 *   • The env-check endpoint returns bypass_payment === true
 *
 * Otherwise renders nothing — never shown to instructors or learners.
 *
 * Polls /api/upload-pricing/admin/env-check once on mount + every 60s
 * so flipping the env var off is reflected without a hard reload.
 *
 * No new database — purely env-driven.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 60_000;

export default function BypassModeBanner({ user }) {
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(
          `${API}/api/upload-pricing/admin/env-check`,
          { withCredentials: true }
        );
        if (!cancelled) setActive(Boolean(r.data?.bypass_payment));
      } catch {
        // Silent — don't add visual noise on transient failures.
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user]);

  if (user?.role !== "admin" || !active) return null;

  return (
    <div
      data-testid="bypass-mode-banner"
      role="status"
      className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/60 px-3 py-2 flex items-start gap-2"
    >
      <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-300 flex-none mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-extrabold text-amber-900 dark:text-amber-100 leading-tight">
          Payment bypass mode is active
        </p>
        <p className="text-[11.5px] text-amber-800/80 dark:text-amber-200/80 leading-snug mt-0.5">
          Uploads are being processed without payment. Remove
          <code className="font-mono mx-1">LESSON_REVIEW_BYPASS_PAYMENT</code>
          from prod env and redeploy when normal billing resumes.
        </p>
      </div>
    </div>
  );
}
